import { Rational } from '@hpd/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import type { FormEvent } from 'react';

import { formatUnits } from '../api/pricing.ts';
import { ApiProblem, meQuery } from '../api/me.ts';
import { jobQuery, jobsQuery } from '../api/jobs.ts';
import {
  acceptQuote,
  dayLabel,
  declineQuote,
  holdLine,
  localLabel,
  quoteQuery,
  quotesQuery,
  removeLine,
  reviseQuote,
  sendQuote,
  setQuoteFulfilment,
  type SiteDetailsInput,
  STATUS_LABELS,
  TYPE_LABELS,
  type Quote,
  type QuoteLine,
  updateQuote,
} from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { renderQuoteDocument } from '../api/documents.ts';
import { DocumentsPanel } from './documents-panel.tsx';
import { Field } from '../components/field.tsx';
import { PageHeader } from '../components/page-header.tsx';
import { formatMinorUnits } from '../money.ts';
import { setQuoteSupplyModel, type SupplyModel } from '../api/agreements.ts';
import { setQuoteVenue, venuesQuery } from '../api/venues.ts';
import { FulfilmentPanel } from './fulfilment-panel.tsx';
import { setQuoteAccountManager } from '../api/account-managers.ts';
import { AccountManagerField } from './account-manager-picker.tsx';
import { SupplyModelField } from './supply-model-field.tsx';
import {
  AllocationPanel,
  EndClientField,
  takesVenueAccount,
  VenueAccountTick,
  VenueMarginPanel,
} from './venue-billing.tsx';
import { VenueField } from './venue-field.tsx';
import {
  shortAdvice,
  VenueEquipmentLabel,
  VenueIncludesWarning,
  venueShortSentence,
} from './venue-equipment.tsx';
import { QuoteInclusions } from './venue-inclusions.tsx';
import { QuoteVenueWarnings } from './venue-warnings.tsx';
import { LineForm } from './quote-line-form.tsx';
import { QuoteLinePicker } from './quote-line-picker.tsx';
import { byRoom, EventTimes, SiteDetailsPanel } from './produced-event.tsx';
import { heldFrom } from '../held-from.ts';

// One quote version (CB-REQ QUO-01, QUO-04, PRC-12, PRC-13 at v1.943). Lines change only while the
// quote is a draft — the database refuses anything else — so a sent quote shows its working and
// offers a revision instead of edits.

export function QuoteScreen() {
  const { quoteId } = useParams({ from: '/authenticated/quotes/$quoteId' });
  const quote = useQuery(quoteQuery(quoteId));
  const me = useQuery(meQuery);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<QuoteLine | null>(null);
  const queryClient = useQueryClient();
  const venue = useMutation({
    mutationFn: (venueId: string | null) => setQuoteVenue(quoteId, venueId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });
  const supplyModel = useMutation({
    mutationFn: (model: SupplyModel) => setQuoteSupplyModel(quoteId, model),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });
  const accountManager = useMutation({
    mutationFn: (id: string | null) => setQuoteAccountManager(quoteId, id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });
  const fulfilment = useMutation({
    mutationFn: (input: Parameters<typeof setQuoteFulfilment>[1]) =>
      setQuoteFulfilment(quoteId, input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });
  const site = useMutation({
    mutationFn: (input: SiteDetailsInput) => updateQuote(quoteId, { site: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });
  const endClient = useMutation({
    mutationFn: (name: string | null) => updateQuote(quoteId, { endClientName: name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteQuery(quoteId).queryKey }),
  });

  const mayManage = me.data?.capabilities.includes('quotes.manage') ?? false;
  // JOB-01 at v1.986: on site may be chosen where the quote's venue keeps our stock.
  const venues = useQuery(venuesQuery);
  const keepsStockHere = !!venues.data?.find((v) => v.id === quote.data?.venue?.id)?.holdsStock;
  const seesPrices = me.data?.canSeePrices ?? false;

  if (quote.isPending) return <p className="text-muted">Loading…</p>;
  if (quote.isError) {
    return (
      <p role="alert" className="alert">
        {quote.error.message}
      </p>
    );
  }
  const q = quote.data;
  const draft = q.status === 'draft';
  const latest = q.lineage.at(-1);
  // QUO-04 at v1.950: a revision of an accepted quote, while the accepted version is in force.
  const inForce = q.lineage.find((v) => v.status === 'accepted' && v.id !== q.id);
  // QUO-04 at v1.953: a revision closed before it was ever sent was withdrawn, not declined.
  const withdrawn = q.status === 'declined' && q.sentAt === null;

  return (
    <div className="flex flex-col gap-6">
      <Link to="/quotes" className="link self-start text-sm">
        ← Quotes
      </Link>
      <PageHeader
        kicker={`Quote · ${q.expired ? 'Expired' : withdrawn ? 'Withdrawn' : STATUS_LABELS[q.status]}`}
        title={`${q.reference}${q.version > 1 ? ` v${q.version}` : ''}`}
        meta={`${TYPE_LABELS[q.type]} · ${q.client.name} · ${q.priceGroup.name} prices`}
        actions={mayManage ? <Actions quote={q} /> : null}
      />

      {q.status === 'superseded' && latest && latest.id !== q.id ? (
        <p className="notice">
          This version was revised.{' '}
          <Link to="/quotes/$quoteId" params={{ quoteId: latest.id }} className="link">
            Open v{latest.version}
          </Link>
        </p>
      ) : null}
      {q.expired ? (
        <p className="alert">
          Valid until {q.validUntil} has passed. It can still be accepted once revised at today’s
          prices.
        </p>
      ) : null}
      {inForce && (q.status === 'draft' || q.status === 'sent') ? (
        <p className="notice">
          This revises{' '}
          <Link to="/quotes/$quoteId" params={{ quoteId: inForce.id }} className="link">
            v{inForce.version}
          </Link>
          , which the client accepted and its job is running under. Nothing changes on the job until
          the client accepts this version too. Lines carried over keep the price agreed; a line you
          change or add is priced at today’s rates.
        </p>
      ) : null}
      {inForce && q.status === 'declined' ? (
        <p className="notice">
          {withdrawn ? 'Withdrawn before it was sent' : 'The client declined this revision'}.{' '}
          <Link to="/quotes/$quoteId" params={{ quoteId: inForce.id }} className="link">
            v{inForce.version}
          </Link>{' '}
          is still in force, and its job carries on under it.
        </p>
      ) : null}
      {inForce && (q.status === 'draft' || q.status === 'sent') ? (
        <RevisionOvertaken inForceId={inForce.id} inForceVersion={inForce.version} draft={draft} />
      ) : null}
      {q.job && q.status === 'accepted' ? <AcceptedNote job={q.job} /> : null}
      {q.status === 'sent' && !q.expired && q.validUntil ? (
        <p className="text-sm text-ink-soft">
          Sent. Prices are fixed; valid until {q.validUntil}. The document itself still goes from
          Booqable during the parallel run.
        </p>
      ) : null}

      <dl
        className={`stat-grid grid-cols-1 ${q.eventWindows ? 'sm:grid-cols-2 lg:grid-cols-4' : 'sm:grid-cols-3'}`}
      >
        {/* JOB-02 at v1.958: a produced event's three windows; its scheduled window spans them. */}
        {q.eventWindows ? (
          <EventTimes windows={q.eventWindows} />
        ) : (
          <>
            <div className="stat">
              <dt className="kicker">Goes out</dt>
              <dd className="mt-1.5 font-extrabold">{localLabel(q.scheduledWindow.localStart)}</dd>
            </div>
            <div className="stat">
              <dt className="kicker">Comes back</dt>
              <dd className="mt-1.5 font-extrabold">{localLabel(q.scheduledWindow.localEnd)}</dd>
            </div>
          </>
        )}
        <div className="stat">
          <dt className="kicker">Days charged</dt>
          <dd className="mt-1.5 font-extrabold">
            {q.soldWindow.days.length}
            <span className="block text-xs font-normal text-muted">
              {q.soldWindow.days.map(dayLabel).join(', ')}
            </span>
          </dd>
        </div>
      </dl>

      {/* QUO-01 at v1.946: entered here and copied to the job; editable until the quote closes. */}
      <FulfilmentPanel
        fulfilment={q.fulfilment}
        editable={mayManage && (q.status === 'draft' || q.status === 'sent')}
        saving={fulfilment.isPending}
        error={fulfilment.error}
        onSave={(input) => fulfilment.mutateAsync(input)}
        onSiteOffered={keepsStockHere}
        changed={q.fulfilmentChanged}
      />

      {/* CLI-01 at v1.984: copied from the client; attribution, so editable at any status. */}
      <AccountManagerField
        current={q.accountManager ?? null}
        editable={mayManage}
        saving={accountManager.isPending}
        error={accountManager.error}
        onChange={(id) => accountManager.mutate(id)}
      />

      {/* VEN-01 at v1.951: like the delivery, editable until the quote closes. */}
      <VenueField
        venue={q.venue ?? null}
        editable={mayManage && (q.status === 'draft' || q.status === 'sent')}
        saving={venue.isPending}
        error={venue.error}
        onChange={(venueId) => venue.mutate(venueId)}
      />
      {/* VEN-11 at v1.988: defaults from the venue's agreement; editable while the quote is open. */}
      <SupplyModelField
        current={q.supplyModel}
        note={q.supplyModelNote}
        editable={mayManage && (q.status === 'draft' || q.status === 'sent')}
        saving={supplyModel.isPending}
        error={supplyModel.error}
        onChange={(model) => supplyModel.mutate(model)}
      />
      {/* VEN-11 at v1.989: named on the venue's invoice beside the requester. */}
      <EndClientField
        value={q.endClientName ?? null}
        shown={q.supplyModel === 'resale'}
        editable={mayManage && (q.status === 'draft' || q.status === 'sent')}
        saving={endClient.isPending}
        error={endClient.error}
        onSave={(name) => endClient.mutate(name)}
      />
      {/* VEN-02 at v1.973: what the venue's access says about these times and this gear. */}
      <QuoteVenueWarnings quoteId={q.id} />
      {/* INV-A14 at v1.983: what the venue's hire includes here, suggested with Add. */}
      <QuoteInclusions quote={q} mayManage={mayManage} />

      {/* JOB-01 at v1.958: like the delivery, editable until the quote closes. */}
      {q.site ? (
        <SiteDetailsPanel
          site={q.site}
          editable={mayManage && (q.status === 'draft' || q.status === 'sent')}
          saving={site.isPending}
          error={site.error}
          onSave={(input) => site.mutateAsync(input)}
        />
      ) : null}

      <section className="flex flex-col gap-4" aria-labelledby="lines">
        <div className="flex items-center justify-between gap-4">
          <h2 id="lines" className="section-title flex-1">
            Lines
          </h2>
          {mayManage && draft && !adding && !editing ? (
            <Button onClick={() => setAdding(true)}>Add a line</Button>
          ) : null}
        </div>
        {adding ? (
          <QuoteLinePicker quote={q} seesPrices={seesPrices} onDone={() => setAdding(false)} />
        ) : null}
        {editing ? (
          <LineForm
            key={editing.id}
            quote={q}
            line={editing}
            seesPrices={seesPrices}
            onDone={() => setEditing(null)}
          />
        ) : null}
        {q.lines.length === 0 ? (
          <p className="text-ink-soft">No lines yet.</p>
        ) : (
          <LinesTable
            quote={q}
            seesPrices={seesPrices}
            editable={mayManage && draft}
            mayHold={mayManage && (q.status === 'draft' || q.status === 'sent')}
            mayTickVenueAccount={
              mayManage &&
              takesVenueAccount(q.supplyModel) &&
              q.status !== 'declined' &&
              q.status !== 'superseded'
            }
            onEdit={setEditing}
          />
        )}
      </section>

      {/* VEN-11 at v1.989 and v1.990: above the price line, behind prices, collapsed. */}
      {seesPrices && q.supplyModel !== 'standard' ? <VenueMarginPanel quoteId={q.id} /> : null}
      {seesPrices && q.venue ? <AllocationPanel quoteId={q.id} /> : null}
      {q.totals ? (
        <dl className="ml-auto grid w-full max-w-sm grid-cols-[1fr_auto] gap-x-6 gap-y-1 border-t-2 border-divider pt-3">
          <dt className="text-ink-soft">Subtotal, ex GST</dt>
          <dd className="text-right tabular-nums">
            {formatMinorUnits(q.totals.subtotal.amountMinor, q.totals.subtotal.currency)}
          </dd>
          {q.totals.taxes
            .filter((t) => t.tax.amountMinor !== '0')
            .map((t) => (
              <div key={t.taxCode} className="contents">
                <dt className="text-ink-soft">{t.taxCode}</dt>
                <dd className="text-right tabular-nums">
                  {formatMinorUnits(t.tax.amountMinor, t.tax.currency)}
                </dd>
              </div>
            ))}
          <dt className="font-extrabold">Total</dt>
          <dd className="text-right text-lg font-extrabold tabular-nums">
            {formatMinorUnits(q.totals.total.amountMinor, q.totals.total.currency)}
          </dd>
        </dl>
      ) : null}

      {/* DOC-01 at v1.959: the quote carries money, so its PDF is above the price line. */}
      {seesPrices ? (
        <DocumentsPanel
          owner="quote"
          id={q.id}
          actions={[{ label: 'Quote PDF', prepare: () => renderQuoteDocument(q.id) }]}
        />
      ) : null}

      {q.lineage.length > 1 ? (
        <section className="flex flex-col gap-2" aria-labelledby="versions">
          <h2 id="versions" className="section-title">
            Versions
          </h2>
          <ul className="flex flex-col gap-1 text-sm">
            {q.lineage.map((v) => (
              <li key={v.id}>
                {v.id === q.id ? (
                  <span className="font-semibold">v{v.version}</span>
                ) : (
                  <Link to="/quotes/$quoteId" params={{ quoteId: v.id }} className="link">
                    v{v.version}
                  </Link>
                )}{' '}
                <span className="text-muted">— {v.status}</span>
              </li>
            ))}
          </ul>
          {q.revisionReason ? (
            <p className="text-sm text-ink-soft">
              Why v{q.version}: {q.revisionReason}
            </p>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}

function LinesTable({
  quote,
  seesPrices,
  editable,
  mayHold,
  mayTickVenueAccount,
  onEdit,
}: {
  quote: Quote;
  seesPrices: boolean;
  editable: boolean;
  mayHold: boolean;
  /** VEN-11 at v1.989: a resale quote's lines for the venue's own account. */
  mayTickVenueAccount: boolean;
  onEdit: (line: QuoteLine) => void;
}) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: (lineId: string) => removeLine(quote.id, lineId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: quoteQuery(quote.id).queryKey });
      await queryClient.invalidateQueries({ queryKey: quotesQuery.queryKey });
    },
  });
  // AVL-03: a refused firm hold leaves the line pencilled, so the quote is re-read either way.
  const hold = useMutation({
    mutationFn: ({ lineId, firm }: { lineId: string; firm: boolean }) =>
      holdLine(quote.id, lineId, firm),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: quoteQuery(quote.id).queryKey });
    },
  });
  // AVL-08: the days a line holds stock (the scheduled window) beside the days it is charged.
  const heldDays = quote.scheduledWindow.days.length;
  const roomed = quote.type === 'produced_event' && quote.lines.some((l) => l.room);
  const groups = roomed ? byRoom(quote.lines) : [{ room: null, lines: [...quote.lines] }];
  const columns = 5 + (seesPrices ? 2 : 0) + (editable || mayHold ? 1 : 0);

  return (
    <div className="overflow-x-auto">
      {remove.isError || hold.isError ? (
        <p role="alert" className="alert mb-2">
          {(remove.error ?? hold.error)?.message}
        </p>
      ) : null}
      <table className="data-table">
        <caption className="sr-only">Quote lines and how each was priced</caption>
        <thead>
          <tr>
            <th scope="col">Item</th>
            <th scope="col" className="text-right">
              Qty
            </th>
            <th scope="col">Charged</th>
            <th scope="col">Stock</th>
            <th scope="col" className="text-right">
              Units
            </th>
            {seesPrices ? (
              <>
                <th scope="col" className="text-right">
                  Rate
                </th>
                <th scope="col" className="text-right">
                  Total ex GST
                </th>
              </>
            ) : null}
            {editable || mayHold ? (
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            ) : null}
          </tr>
        </thead>
        {/* JOB-14 at v1.958: a produced event's lines under their rooms, those with none last. */}
        {groups.map((group) => (
          <tbody key={group.room?.id ?? 'none'}>
            {roomed ? (
              <tr>
                <th scope="colgroup" colSpan={columns} className="kicker pt-4">
                  {group.room?.name ?? 'No room'}
                </th>
              </tr>
            ) : null}
            {group.lines.map((l) => (
              <tr key={l.id}>
                <th scope="row" className="font-semibold">
                  {l.description}
                  <VenueEquipmentLabel venueEquipment={l.venueEquipment} />
                  <VenueIncludesWarning venueIncludes={l.venueIncludes} />
                  {/* The venue's own gear is sold for it, never for its own account. */}
                  {takesVenueAccount(quote.supplyModel) && !l.venueEquipment ? (
                    <VenueAccountTick
                      quoteId={quote.id}
                      lineId={l.id}
                      checked={l.forVenueAccount}
                      editable={mayTickVenueAccount}
                    />
                  ) : null}
                  <span className="block text-xs font-normal text-muted">
                    {l.structure.name}
                    {l.taxCode !== 'GST' ? ` · ${l.taxCode}` : ''}
                  </span>
                </th>
                <td className="text-right tabular-nums">{l.quantity}</td>
                <td className="text-sm">
                  {l.effectiveDays ? (
                    <>
                      {l.effectiveDays.length} {l.effectiveDays.length === 1 ? 'day' : 'days'}
                      {l.chargeableDays ? (
                        <span className="block text-xs text-muted">
                          {l.effectiveDays.map(dayLabel).join(', ')}
                        </span>
                      ) : null}
                      {l.availability && heldDays !== l.effectiveDays.length ? (
                        <span className="block text-xs text-muted">
                          holds stock {heldDays} {heldDays === 1 ? 'day' : 'days'}
                        </span>
                      ) : null}
                    </>
                  ) : l.enteredMeasure ? (
                    `${formatUnits(ratio(l.enteredMeasure))} ${l.unit === 'hour' ? 'h' : 'km'}`
                  ) : (
                    'Flat'
                  )}
                </td>
                <td className="text-sm">
                  <StockCell line={l} />
                </td>
                <td className="text-right tabular-nums">{formatUnits(ratio(l.units))}</td>
                {seesPrices ? (
                  <>
                    <td className="text-right tabular-nums">
                      {l.rate ? formatMinorUnits(l.rate.amountMinor, l.rate.currency) : '—'}
                      {l.rateInherited ? (
                        <span className="block text-xs text-muted">default group’s rate</span>
                      ) : null}
                    </td>
                    <td className="text-right tabular-nums">
                      {l.total ? formatMinorUnits(l.total.amountMinor, l.total.currency) : '—'}
                      {l.discount ? (
                        <span className="block text-xs text-muted">
                          less{' '}
                          {l.discount.kind === 'percent' && l.discount.fraction
                            ? `${(l.discount.fraction.numerator * 100) / l.discount.fraction.denominator}%`
                            : l.discount.amount
                              ? formatMinorUnits(
                                  l.discount.amount.amountMinor,
                                  l.discount.amount.currency,
                                )
                              : ''}{' '}
                          — {l.discount.reason}
                        </span>
                      ) : null}
                    </td>
                  </>
                ) : null}
                {editable || mayHold ? (
                  <td className="text-right text-sm whitespace-nowrap">
                    {mayHold && l.availability ? (
                      <button
                        type="button"
                        className="link mr-2 cursor-pointer"
                        disabled={hold.isPending}
                        onClick={() =>
                          hold.mutate({ lineId: l.id, firm: l.availability?.hold !== 'firm' })
                        }
                      >
                        {l.availability.hold === 'firm' ? 'Pencil' : 'Hold'}
                        <span className="sr-only"> {l.description}</span>
                      </button>
                    ) : null}
                    {editable ? (
                      <>
                        <EditButton what={l.description} onClick={() => onEdit(l)} />
                        <RemoveButton
                          what={l.description}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(l.id)}
                        />
                      </>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/**
 * QUO-03 / AVL-03 at v1.944: what a line holds and whether it is short. A pencil blocks nothing, so
 * pencils elsewhere are a note, not a shortage; firm holds elsewhere are what make a line short.
 */
function StockCell({ line }: { line: QuoteLine }) {
  const a = line.availability;
  if (!a) return <span className="text-muted">—</span>;
  // INV-A14: another venue's equipment holds nothing here, which is not a shortage to resolve.
  if (line.venueEquipment && !line.venueEquipment.here) {
    return <span className="text-xs">Not held: it stays at {line.venueEquipment.venueName}</span>;
  }
  const pencilled = a.clashes.filter((c) => !c.firm);
  const firm = a.clashes.filter((c) => c.firm);
  return (
    <div className="flex flex-col gap-1">
      <span className="flex flex-wrap gap-1">
        <span className={a.hold === 'firm' ? 'tag tag-accent' : 'tag'}>
          {a.hold === 'firm' ? 'Held firmly' : 'Pencilled'}
        </span>
        {a.short > 0 ? (
          <span className="tag tag-accent-2 font-semibold">Short {a.short}</span>
        ) : null}
      </span>
      {heldFrom(a.from) ? <span className="text-xs">{heldFrom(a.from)}</span> : null}
      <span className="text-xs text-muted">
        {a.onHand} on hand
        {a.firmElsewhere > 0 ? ` · ${a.firmElsewhere} held firmly elsewhere` : ''}
        {a.pencilledElsewhere > 0 ? ` · ${a.pencilledElsewhere} pencilled elsewhere` : ''}
      </span>
      {firm.length + pencilled.length > 0 ? (
        <span className="text-xs">
          {[...firm, ...pencilled].map((c, i) => (
            <span key={c.quoteId}>
              {i > 0 ? ', ' : ''}
              {/* A hold on an accepted quote is its job's: name the job, which is where to look. */}
              {c.job ? (
                <Link to="/jobs/$jobId" params={{ jobId: c.job.id }} className="link">
                  {c.job.reference}
                </Link>
              ) : (
                <>
                  <Link to="/quotes/$quoteId" params={{ quoteId: c.quoteId }} className="link">
                    {c.reference}
                    {c.version > 1 ? ` v${c.version}` : ''}
                  </Link>
                  {c.firm ? ' (firm)' : ''}
                  {/* AVL-03 at v1.945: an expired quote's claim is weak, not gone; declining it
                      ends it. */}
                  {c.expired ? ' (expired)' : ''}
                </>
              )}
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

/** QUO-03: the API's "lines are short — send anyway?" refusal. */
function isShortages(error: unknown): boolean {
  return error instanceof ApiProblem && error.type.endsWith(':shortages');
}

/** The view carries a fraction as two integers; formatUnits reads the domain's Rational. */
function ratio(r: { numerator: number; denominator: number }): Rational {
  return Rational.of(BigInt(r.numerator), BigInt(r.denominator));
}

/**
 * QUO-04 at v1.953: an open revision whose job has moved on — gear gone out, or the job cancelled —
 * can never be accepted, so it is not sent either. Say so here rather than only when a button is
 * pressed. Reads the version in force for its job, then the job for how far it has got.
 */
function RevisionOvertaken({
  inForceId,
  inForceVersion,
  draft,
}: {
  inForceId: string;
  inForceVersion: number;
  draft: boolean;
}) {
  const inForce = useQuery(quoteQuery(inForceId));
  const jobId = inForce.data?.job?.id;
  const job = useQuery({ ...jobQuery(jobId ?? ''), enabled: Boolean(jobId) });
  const status = job.data?.status;
  if (!job.data || (status !== 'out' && status !== 'returned' && status !== 'cancelled')) {
    return null;
  }
  return (
    <p className="alert">
      {status === 'cancelled' ? (
        <>
          <Link to="/jobs/$jobId" params={{ jobId: job.data.id }} className="link">
            {job.data.reference}
          </Link>{' '}
          is cancelled
        </>
      ) : (
        <>
          Gear on{' '}
          <Link to="/jobs/$jobId" params={{ jobId: job.data.id }} className="link">
            {job.data.reference}
          </Link>{' '}
          has gone out
        </>
      )}
      , so this revision can no longer be {draft ? 'sent or ' : ''}accepted.{' '}
      {draft ? 'Withdraw' : 'Decline'} it; the job carries on under v{inForceVersion}.
    </p>
  );
}

/** QUO-01: an accepted quote names its job, and says so if that job has since been cancelled. */
function AcceptedNote({ job: ref }: { job: { id: string; reference: string } }) {
  const job = useQuery(jobQuery(ref.id));
  const link = (
    <Link to="/jobs/$jobId" params={{ jobId: ref.id }} className="link">
      {ref.reference}
    </Link>
  );
  if (job.data?.status === 'cancelled') {
    return (
      <p className="notice">Accepted, and became job {link}, which has since been cancelled.</p>
    );
  }
  return (
    <p className="notice">
      Accepted. This quote is now job {link}, which has its own copy of the dates, the delivery and
      the lines.
    </p>
  );
}

/** Why an accepted quote can't be revised now, or null if it can. */
function useWhyNotRevised(quote: Quote): string | null {
  const jobId = quote.status === 'accepted' ? quote.job?.id : undefined;
  const job = useQuery({ ...jobQuery(jobId ?? ''), enabled: Boolean(jobId) });
  if (!jobId || !job.data) return null;
  const ref = job.data.reference;
  if (job.data.status === 'cancelled')
    return `${ref} is cancelled, so this quote can’t be revised.`;
  if (job.data.status !== 'confirmed') {
    return `Gear on ${ref} has gone out, so this quote can’t be revised. A change now is a variation, which arrives in Phase 2.`;
  }
  // QUO-04 at v1.993: an issued invoice no longer stops a revision; accepting one credits it.
  return null;
}

function Actions({ quote }: { quote: Quote }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const whyNotRevised = useWhyNotRevised(quote);
  const [revising, setRevising] = useState(false);
  // QUO-04: this version revises an accepted one, so accepting moves the job rather than making one.
  const isRevision = quote.lineage.some((v) => v.status === 'accepted' && v.id !== quote.id);
  const [reason, setReason] = useState('');

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: quoteQuery(quote.id).queryKey });
    await queryClient.invalidateQueries({ queryKey: quotesQuery.queryKey });
  };
  // QUO-03 at v1.944: a short quote asks before it is marked sent, because the shortage may be
  // deliberate — gear to be cross-hired. The API asks too; this just asks first.
  const [confirmingShort, setConfirmingShort] = useState(false);
  const short = shortAdvice(quote.lines, quote.shortLines);
  const send = useMutation({
    mutationFn: (acknowledge: boolean) => sendQuote(quote.id, acknowledge),
    onSuccess: async () => {
      setConfirmingShort(false);
      await refresh();
    },
    // Another quote may have firmed up since this one was read: the API's question is asked here.
    onError: async (error) => {
      if (isShortages(error)) {
        setConfirmingShort(true);
        await refresh();
      }
    },
  });
  // QUO-01 at v1.946: acceptance creates the job. Short lines always ask first — it is a
  // commitment — and then stay pencilled on the job (AVL-03).
  const [accepting, setAccepting] = useState(false);
  const [clientPo, setClientPo] = useState('');
  const accept = useMutation({
    mutationFn: () =>
      acceptQuote(quote.id, {
        // The form below is the question when the quote shows short; it acknowledges only then.
        acknowledgeShortages: quote.shortLines > 0,
        ...(clientPo.trim() ? { clientPo: clientPo.trim() } : {}),
      }),
    // Another quote may have firmed up since this one was read. Re-reading shows the shortage in
    // the form, which then asks the API's question.
    onError: async (error) => {
      if (isShortages(error)) await refresh();
    },
    onSuccess: async (accepted) => {
      await refresh();
      await queryClient.invalidateQueries({ queryKey: jobsQuery.queryKey });
      if (accepted.job) await navigate({ to: '/jobs/$jobId', params: { jobId: accepted.job.id } });
    },
  });
  // Declining, or withdrawing a draft revision, can't be undone, so it asks once first.
  const [closing, setClosing] = useState(false);
  const decline = useMutation({
    mutationFn: () => declineQuote(quote.id),
    onSuccess: async () => {
      setClosing(false);
      await refresh();
    },
  });
  const revise = useMutation({
    mutationFn: () => reviseQuote(quote.id, reason.trim()),
    onSuccess: async (next) => {
      await refresh();
      setRevising(false);
      await navigate({ to: '/quotes/$quoteId', params: { quoteId: next.id } });
    },
  });
  const failure =
    (isShortages(send.error) ? null : send.error) ??
    (isShortages(accept.error) ? null : accept.error) ??
    decline.error ??
    revise.error;

  function submitRevision(event: FormEvent) {
    event.preventDefault();
    if (reason.trim()) revise.mutate();
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex flex-wrap gap-3">
        {/* Only offered once there is something to send; the API refuses an empty quote too. */}
        {quote.status === 'draft' && quote.lines.length > 0 ? (
          <Button
            onClick={() =>
              // QUO-03 at v1.945: whether a short quote asks first is the tenant's setting.
              quote.shortLines > 0 && quote.confirmShortOnSend
                ? setConfirmingShort(true)
                : send.mutate(false)
            }
            disabled={send.isPending}
          >
            {send.isPending ? 'Marking…' : 'Mark sent'}
          </Button>
        ) : null}
        {/* QUO-04 at v1.953: a draft revision nobody wants is withdrawn. The client never saw it,
            so it is not "declined"; the API records it as a decline that was never sent. */}
        {quote.status === 'draft' && isRevision && !closing ? (
          <Button variant="quiet" onClick={() => setClosing(true)}>
            Withdraw…
          </Button>
        ) : null}
        {quote.status === 'sent' && !quote.expired && !accepting ? (
          <Button onClick={() => setAccepting(true)}>Mark accepted…</Button>
        ) : null}
        {/* QUO-04 at v1.950: an accepted quote is revised until its gear goes out, while its
            invoice is a draft, and unless its job is cancelled. Where it can't be, the reason
            stands in the button's place (the operator's decision, 2026-09-30). */}
        {quote.status === 'accepted' ? (
          whyNotRevised ? (
            <p className="max-w-sm text-right text-sm text-ink-soft">{whyNotRevised}</p>
          ) : (
            <Button variant="quiet" onClick={() => setRevising(true)}>
              Revise…
            </Button>
          )
        ) : null}
        {quote.status === 'sent' ? (
          <>
            <Button variant="quiet" onClick={() => setRevising(true)}>
              Revise…
            </Button>
            {closing ? null : (
              <Button variant="quiet" onClick={() => setClosing(true)}>
                Mark declined…
              </Button>
            )}
          </>
        ) : null}
      </div>
      {closing ? (
        <div
          role="alertdialog"
          aria-label={quote.status === 'draft' ? 'Withdraw this revision' : 'Mark declined'}
          className="notice flex flex-col gap-2"
        >
          <p>
            {quote.status === 'draft'
              ? `Withdraw ${quote.reference} v${quote.version}? It was never sent, and it can’t be undone. The version in force carries on.`
              : `Mark ${quote.reference}${quote.version > 1 ? ` v${quote.version}` : ''} declined by the client? It can’t be undone${isRevision ? '; the version in force carries on' : ''}.`}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => decline.mutate()} disabled={decline.isPending}>
              {decline.isPending
                ? 'Saving…'
                : quote.status === 'draft'
                  ? 'Withdraw it'
                  : 'Mark declined'}
            </Button>
            <Button variant="quiet" onClick={() => setClosing(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {confirmingShort ? (
        <div role="alertdialog" aria-label="Lines are short" className="alert flex flex-col gap-2">
          <p>
            {short.own > 0
              ? `${short.own} ${short.own === 1 ? 'line is' : 'lines are'} short against firm holds elsewhere. Send anyway if the gear will be cross-hired. `
              : ''}
            {venueShortSentence(short.venue)}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => send.mutate(true)} disabled={send.isPending}>
              Send anyway
            </Button>
            <Button variant="quiet" onClick={() => setConfirmingShort(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {accepting ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            accept.mutate();
          }}
          role={quote.shortLines > 0 ? 'alertdialog' : undefined}
          aria-label="Accept this quote"
          className={`${quote.shortLines > 0 ? 'alert' : 'notice'} flex flex-col gap-3`}
        >
          <p>
            {isRevision
              ? 'Accepting moves the job onto this version: its lines, dates and holds follow it. If its invoice has issued, that invoice gets a credit for all of it, to issue.'
              : 'Accepting creates the job and reserves the gear.'}
            {short.own > 0
              ? ` ${short.own} ${short.own === 1 ? 'line is' : 'lines are'} short: ${
                  short.own === 1 ? 'it' : 'they'
                } will stay pencilled on the job, to be reserved or cross-hired before the gear leaves.`
              : ''}
            {short.venue > 0 ? ` ${venueShortSentence(short.venue)}` : ''}
          </p>
          <Field
            label="Client PO (optional)"
            value={clientPo}
            onChange={(e) => setClientPo(e.currentTarget.value)}
          />
          <div className="flex gap-2">
            <Button type="submit" disabled={accept.isPending}>
              {accept.isPending
                ? 'Accepting…'
                : quote.shortLines > 0
                  ? 'Accept anyway'
                  : isRevision
                    ? 'Accept this version'
                    : 'Accept and create job'}
            </Button>
            <Button type="button" variant="quiet" onClick={() => setAccepting(false)}>
              Cancel
            </Button>
          </div>
        </form>
      ) : null}
      {revising ? (
        <form onSubmit={submitRevision} className="flex items-end gap-2">
          <Field
            label="Why revise?"
            required
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
          />
          <Button type="submit" disabled={revise.isPending}>
            {revise.isPending ? 'Revising…' : 'Revise'}
          </Button>
        </form>
      ) : null}
      {failure ? (
        <p role="alert" className="text-sm text-accent-2-700">
          {failure.message}
        </p>
      ) : null}
    </div>
  );
}
